/** @jsxImportSource @emotion/react */
import { Icon, IconName, Position, Tooltip } from "@blueprintjs/core"
import { useState } from "react"

import { Side } from "./types/common"
import { Order } from "./types/order"
import {
  ActionBadge,
  ControlsContainer,
  CountIndicator,
  NoItems,
  RotatingCaret,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  TickerGridContainer,
  TickerItem,
  TickersContent,
  TickersGrid,
} from "./WaveTickerGrid.styled"

interface IWaveTickerGridProps {
  orders: Order[]
  isWaveDetail?: boolean
}

// Title formatting for sections
const formatSideLabel = (side: Side): string => {
  return side.replace("_", " ").toUpperCase()
}

const WaveTickerGrid = ({ orders, isWaveDetail = false }: IWaveTickerGridProps) => {
  // State to track which sections are collapsed
  const [collapsedSections, setCollapsedSections] = useState<Record<Side, boolean>>({
    [Side.BUY]: true,
    [Side.BUY_COVER]: true,
    [Side.SELL]: true,
    [Side.SELL_SHORT]: true,
  })

  // Toggle collapsed state for a section
  const toggleSection = (side: Side) => {
    setCollapsedSections(prev => ({
      ...prev,
      [side]: !prev[side],
    }))
  }

  // Filter orders by side
  const getOrdersBySide = (side: Side) => {
    return orders.filter(order => order.side === side)
  }

  // Render a single section (quadrant)
  const renderSection = (side: Side) => {
    const sideOrders = getOrdersBySide(side)
    const isCollapsed = collapsedSections[side]

    return (
      <SectionContainer type={side} isCollapsed={isCollapsed}>
        <SectionHeader type={side} onClick={() => toggleSection(side)}>
          <SectionTitle>
            <ActionBadge type={side}>{formatSideLabel(side)}</ActionBadge>
            <CountIndicator>{sideOrders.length}</CountIndicator>
          </SectionTitle>
          <ControlsContainer>
            <RotatingCaret isCollapsed={isCollapsed}>
              <Icon icon="chevron-down" size={16} />
            </RotatingCaret>
          </ControlsContainer>
        </SectionHeader>

        <TickersContent isCollapsed={isCollapsed}>
          {sideOrders.length === 0 ? (
            <NoItems>No {formatSideLabel(side)} orders</NoItems>
          ) : (
            <TickersGrid>
              {sideOrders.map(order => (
                <Tooltip
                  key={order.id}
                  content={
                    <div>
                      <div>Symbol: {order.instrument?.bloomberg_code}</div>
                      {order.amount_usd != null && (
                        <div>
                          Amount USD: $
                          {Intl.NumberFormat("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(Number(order.amount_usd))}
                        </div>
                      )}
                      {order.quantity != null && <div>Quantity: {order.quantity}</div>}
                      <div>Type: {order.order_type}</div>
                      <div>Broker: {order.broker}</div>
                      <div>Execution: {order.execution_type?.replace("_", " ").toUpperCase()}</div>
                      <div>Summary: {order.execution_summary}</div>
                      <div>Status: {order.status}</div>
                    </div>
                  }
                  position={Position.TOP}
                >
                  <TickerItem type={side}>
                    {order.instrument?.bloomberg_code?.replace(" Equity", "")}
                  </TickerItem>
                </Tooltip>
              ))}
            </TickersGrid>
          )}
        </TickersContent>
      </SectionContainer>
    )
  }

  return (
    <TickerGridContainer>
      {renderSection(Side.BUY)}
      {renderSection(Side.BUY_COVER)}
      {renderSection(Side.SELL)}
      {renderSection(Side.SELL_SHORT)}
    </TickerGridContainer>
  )
}

export default WaveTickerGrid
